import React, { useEffect, useState } from 'react';
import { Loader2, PenLine, Save } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { RichTextEditor } from './RichTextEditor';
import { WelcomeStepsEditor } from './WelcomeStepsEditor';
import { clientWelcomeAPI } from '../lib/api';

const BANK = [['titular', 'Titular'], ['iban', 'IBAN'], ['banco', 'Banco'], ['concepto', 'Concepto a indicar']];

export const WelcomeSettingsDialog = ({ onSaved }) => {
  const [open, setOpen] = useState(false);
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setS(null);
    clientWelcomeAPI.getSettings().then(setS).catch((e) => toast.error(e.message));
  }, [open]);

  const set = (patch) => setS((prev) => ({ ...prev, ...patch }));
  const save = async () => {
    setSaving(true);
    try {
      const { mensaje_html, portal_html, iban, titular, banco, concepto } = s;
      const pasos = s.pasos.filter((p) => p.titulo.trim());
      await clientWelcomeAPI.saveSettings({ mensaje_html, portal_html, iban, titular, banco, concepto, pasos });
      toast.success('Bienvenida guardada para todos los clientes');
      setOpen(false);
      onSaved?.();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} data-testid="welcome-settings-open-btn"><PenLine className="w-4 h-4 mr-2" />Editar textos (todos los clientes)</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="welcome-settings-dialog">
          <DialogHeader>
            <DialogTitle>Bienvenida del portal de cliente</DialogTitle>
            <DialogDescription>Estos textos son los mismos para todos los clientes.</DialogDescription>
          </DialogHeader>
          {!s ? <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div> : (
            <div className="space-y-5">
              <div className="space-y-1.5"><Label>Mensaje de bienvenida</Label>
                <RichTextEditor value={s.mensaje_html} onChange={(v) => set({ mensaje_html: v })} testId="ws-message-editor" minHeight={140} sticky={false} /></div>
              <div className="space-y-1.5"><Label>Primeros pasos (el cliente los marca)</Label>
                <WelcomeStepsEditor pasos={s.pasos} onChange={(pasos) => set({ pasos })} /></div>
              <div className="space-y-1.5"><Label>Cómo funciona el portal</Label>
                <RichTextEditor value={s.portal_html} onChange={(v) => set({ portal_html: v })} testId="ws-portal-editor" minHeight={140} sticky={false} /></div>
              <div className="space-y-1.5"><Label>Datos bancarios (se muestran a clientes con transferencia)</Label>
                <div className="grid sm:grid-cols-2 gap-2">
                  {BANK.map(([k, l]) => <Input key={k} value={s[k] || ''} onChange={(e) => set({ [k]: e.target.value })} placeholder={l} data-testid={`ws-bank-${k}`} />)}
                </div></div>
            </div>
          )}
          <div className="flex justify-end gap-2 sticky -bottom-6 bg-background py-3 border-t">
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} disabled={!s || saving} data-testid="ws-save-btn">{saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Guardar</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
